import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasteableInput } from "@/components/PasteableInput";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Plus, Trash, ArrowsClockwise, Copy, Lock, PencilSimple, ClockCounterClockwise, DesktopTower, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, ALL_CAPABILITIES } from "@/context/AuthContext";
import { passwordPolicyError, PASSWORD_HINT } from "@/lib/password";

const ROLE_OPTS = [
  { v: "super_admin", label: "Super Administrador", color: "#002FA7" },
  { v: "inventory",   label: "Inventario",          color: "#00873D" },
  { v: "sales",       label: "Ventas",              color: "#FFC000" },
  { v: "marketing",   label: "Marketing",           color: "#E91E63" },
  { v: "technician",  label: "Técnico",             color: "#7c3aed" },
  { v: "taller",      label: "Taller (Hospital)",   color: "#0891b2" },
];

const EMPTY_FORM = { email: "", name: "", roles: ["sales"], capabilities: [], is_commissioner: false, commission_pct: 10 };

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const parseDevice = (ua) => {
  if (!ua) return "Desconocido";
  const s = ua.toLowerCase();
  let os = "PC";
  if (s.includes("iphone")) os = "iPhone";
  else if (s.includes("ipad")) os = "iPad";
  else if (s.includes("android")) os = "Android";
  else if (s.includes("mac os")) os = "Mac";
  else if (s.includes("windows")) os = "Windows";
  else if (s.includes("linux")) os = "Linux";
  let br = "";
  if (s.includes("edg/")) br = "Edge";
  else if (s.includes("chrome/")) br = "Chrome";
  else if (s.includes("safari/")) br = "Safari";
  else if (s.includes("firefox/")) br = "Firefox";
  return `${os}${br ? ` · ${br}` : ""}`;
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [tempInfo, setTempInfo] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsUser, setSessionsUser] = useState(null); // when set, opens drawer

  const load = async () => setUsers((await api.get("/users")).data);
  const loadSessions = async () => {
    try {
      const { data } = await api.get("/users/sessions", { params: { limit: 50 } });
      setSessions(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    loadSessions();
    // Poll every 20s to keep live-status fresh
    const t = setInterval(() => { load(); loadSessions(); }, 20000);
    return () => clearInterval(t);
  }, []);

  // A user is considered live if seen within the last 2 minutes
  const isLive = (iso) => {
    if (!iso) return false;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return false;
    return (Date.now() - t) < 120000;
  };

  const toggleRole = (r) => setForm((f) => ({
    ...f,
    roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r],
  }));
  const toggleCap = (c) => setForm((f) => ({
    ...f,
    capabilities: (f.capabilities || []).includes(c)
      ? (f.capabilities || []).filter((x) => x !== c)
      : [...(f.capabilities || []), c],
  }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.roles.length) return toast.error("Selecciona al menos un rol");
    setSaving(true);
    try {
      if (editingUserId) {
        await api.put(`/users/${editingUserId}`, {
          name: form.name,
          roles: form.roles,
          capabilities: form.capabilities || [],
          is_commissioner: !!form.is_commissioner,
          commission_pct: Number(form.commission_pct) || 10,
        });
        toast.success("Usuario actualizado");
      } else {
        const { data } = await api.post("/users", {
          ...form,
          commission_pct: Number(form.commission_pct) || 10,
        });
        setTempInfo({ email: data.email, password: data.temp_password, roles: data.roles || [data.role] });
      }
      setForm(EMPTY_FORM);
      setEditingUserId(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const openEdit = (u) => {
    setEditingUserId(u.id);
    setForm({
      email: u.email,
      name: u.name,
      roles: u.roles && u.roles.length ? [...u.roles] : [u.role],
      capabilities: Array.isArray(u.capabilities) ? [...u.capabilities] : [],
      is_commissioner: !!u.is_commissioner,
      commission_pct: Number(u.commission_pct ?? 10),
    });
    setShowForm(true);
  };
  const openNew = () => { setEditingUserId(null); setForm(EMPTY_FORM); setShowForm(true); };

  const logoutAll = async (u) => {
    if (!window.confirm(`¿Cerrar todas las sesiones de ${u.name || u.email}? Tendrá que iniciar sesión de nuevo en todos sus dispositivos.`)) return;
    try {
      await api.post(`/users/${u.id}/logout-all`);
      toast.success("Sesiones cerradas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const resetPwd = async (uid) => {
    try {
      const { data } = await api.post(`/users/${uid}/reset-password`);
      const u = users.find((x) => x.id === uid);
      setTempInfo({ email: u.email, password: data.temp_password, roles: u.roles || [u.role] });
      load();
    } catch { toast.error("Error"); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setToDelete(null); }
  };

  const submitChangePwd = async (e) => {
    e.preventDefault();
    if (pwdForm.next !== pwdForm.confirm) {
      toast.error("Las contraseñas nuevas no coinciden");
      return;
    }
    const policyErr = passwordPolicyError(pwdForm.next);
    if (policyErr) {
      toast.error(policyErr);
      return;
    }
    setPwdSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: pwdForm.current,
        new_password: pwdForm.next,
      });
      if (data?.token) localStorage.setItem("inv_token", data.token);
      toast.success("Contraseña actualizada. Tus otras sesiones se cerraron.");
      setShowChangePwd(false);
      setPwdForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cambiar contraseña");
    } finally {
      setPwdSaving(false);
    }
  };

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success("Copiado");
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">ACCESO</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Usuarios</h1>
          <p className="text-zinc-500 mt-1 text-sm">{users.length} cuentas registradas</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={()=>setShowChangePwd(true)} variant="outline" data-testid="my-change-pwd-btn" className="gap-2">
            <Lock size={16} weight="bold" /> Cambiar mi contraseña
          </Button>
          <Button onClick={openNew} data-testid="new-user-btn" className="bg-[#002FA7] hover:bg-[#002277] text-white gap-2">
            <Plus size={16} weight="bold" /> Nuevo usuario
          </Button>
        </div>
      </header>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Nombre</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Roles</th>
              <th className="text-left px-4 py-3">Última sesión</th>
              <th className="text-left px-4 py-3">Estado</th>
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const userRolesArr = (u.roles && u.roles.length) ? u.roles : (u.role ? [u.role] : []);
              return (
                <tr key={u.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-zinc-700">{u.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {userRolesArr.map((r) => {
                        const opt = ROLE_OPTS.find((x) => x.v === r) || { label: r, color: "#52525B" };
                        return (
                          <span key={r} className="text-xs font-semibold px-2 py-1 rounded-sm" style={{ background: opt.color + "20", color: opt.color }}>
                            {opt.label}
                          </span>
                        );
                      })}
                      {(u.capabilities || []).map((c) => {
                        const meta = ALL_CAPABILITIES.find((x) => x.key === c);
                        return (
                          <span key={c} className="text-[10px] font-semibold px-2 py-1 rounded-sm bg-zinc-100 text-zinc-700 border border-zinc-200" title={meta?.description || c}>
                            + {meta?.label || c}
                          </span>
                        );
                      })}
                      {u.is_commissioner && (
                        <span
                          className="text-[10px] font-semibold px-2 py-1 rounded-sm border"
                          style={{ background: "#E91E6320", color: "#E91E63", borderColor: "#E91E6340" }}
                          title={`Comisionista · ${Number(u.commission_pct ?? 10)}% sobre ventas cobradas`}
                          data-testid={`user-commission-badge-${u.id}`}
                        >
                          %{Number(u.commission_pct ?? 10)} comisión
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 text-xs font-mono-data">
                    {u.last_login_at ? fmtDate(u.last_login_at) : <span className="text-zinc-400 italic">nunca</span>}
                  </td>
                  <td className="px-4 py-3">
                    {isLive(u.last_seen_at) ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-[#00873D] font-semibold" data-testid={`live-${u.id}`} title={`Vista por última vez: ${fmtDate(u.last_seen_at)}`}>
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00873D] opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00873D]"></span>
                        </span>
                        En línea
                      </span>
                    ) : u.must_change_password
                      ? <span className="text-xs text-[#FFC000] font-medium">⚠ Temporal (debe cambiar)</span>
                      : <span className="text-xs text-zinc-500 font-medium">○ Inactivo</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={()=>setSessionsUser(u)} title="Ver historial de sesiones" data-testid={`sessions-${u.id}`} className="p-2 hover:bg-zinc-200 rounded">
                        <ClockCounterClockwise size={15} />
                      </button>
                      <button onClick={()=>openEdit(u)} title="Editar nombre y roles" data-testid={`edit-user-${u.id}`} className="p-2 hover:bg-zinc-200 rounded">
                        <PencilSimple size={15} />
                      </button>
                      <button onClick={()=>resetPwd(u.id)} title="Resetear contraseña — genera una nueva temporal" data-testid={`reset-pwd-${u.id}`} className="p-2 hover:bg-zinc-200 rounded">
                        <ArrowsClockwise size={15} />
                      </button>
                      {u.id !== me.id && (
                        <button onClick={()=>logoutAll(u)} title="Cerrar todas sus sesiones" data-testid={`logout-all-${u.id}`} className="p-2 hover:bg-amber-100 text-amber-700 rounded">
                          <SignOut size={15} />
                        </button>
                      )}
                      {u.id !== me.id && (
                        <button onClick={()=>setToDelete(u)} title="Eliminar" data-testid={`del-user-${u.id}`} className="p-2 hover:bg-red-100 text-[#E0000F] rounded">
                          <Trash size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recent login sessions across the team */}
      <section className="border border-zinc-200 bg-white" data-testid="recent-sessions">
        <header className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2">
              <ClockCounterClockwise size={18} weight="duotone" className="text-[#002FA7]" />
              Últimas sesiones
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">Historial de inicios de sesión del equipo (más recientes primero).</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadSessions} className="gap-1.5" data-testid="refresh-sessions">
            <ArrowsClockwise size={14} /> Actualizar
          </Button>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Fecha y hora</th>
                <th className="text-left px-4 py-3">Usuario</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Dispositivo</th>
                <th className="text-left px-4 py-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-zinc-400 dotted-bg">Sin sesiones registradas todavía</td></tr>
              )}
              {sessions.map((s) => (
                <tr key={s.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-2.5 text-zinc-700 font-mono-data text-xs">{fmtDate(s.created_at)}</td>
                  <td className="px-4 py-2.5 font-medium text-zinc-800">{s.user_name || "—"}</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-xs">{s.user_email}</td>
                  <td className="px-4 py-2.5 text-zinc-700 text-xs">
                    <span className="inline-flex items-center gap-1" title={s.user_agent}>
                      <DesktopTower size={12} className="text-zinc-400" /> {parseDevice(s.user_agent)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-500 font-mono-data text-xs">{s.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Per-user sessions drawer */}
      <Sheet open={!!sessionsUser} onOpenChange={(o)=>{ if (!o) setSessionsUser(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle className="font-heading text-2xl">
              Sesiones de {sessionsUser?.name}
            </SheetTitle>
            <SheetDescription>{sessionsUser?.email}</SheetDescription>
          </SheetHeader>
          <UserSessions userId={sessionsUser?.id} />
        </SheetContent>
      </Sheet>

      <Dialog open={showForm} onOpenChange={(o)=>{ setShowForm(o); if (!o) { setEditingUserId(null); setForm(EMPTY_FORM); } }}>
        <DialogContent data-testid="user-form-dialog" className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-100 shrink-0">
            <DialogTitle className="font-heading text-2xl">{editingUserId ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-5">
              {/* Datos básicos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nombre completo</Label>
                  <Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} required data-testid="user-name" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Email</Label>
                  <PasteableInput type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} required data-testid="user-email-input" className="mt-1" disabled={!!editingUserId} />
                  {editingUserId && <p className="text-[10px] text-zinc-400 mt-1">El email no se puede cambiar.</p>}
                </div>
              </div>

              {/* Roles + Facultades en 2 columnas en desktop */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* Roles */}
                <div className="lg:col-span-4">
                  <Label className="text-xs uppercase tracking-wider">Roles</Label>
                  <p className="text-[10px] text-zinc-400 mt-1 mb-2">Puedes elegir varios.</p>
                  <div className="space-y-1.5">
                    {ROLE_OPTS.map((r) => {
                      const checked = form.roles.includes(r.v);
                      return (
                        <label key={r.v} className="flex items-center gap-2 cursor-pointer p-2 border border-zinc-200 rounded hover:bg-zinc-50 has-[:checked]:border-[#002FA7]/40 has-[:checked]:bg-[#002FA7]/5" data-testid={`user-role-${r.v}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRole(r.v)}
                            className="w-4 h-4 accent-[#002FA7]"
                          />
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-sm" style={{ background: r.color + "20", color: r.color }}>
                            {r.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Facultades */}
                <div className="lg:col-span-8">
                  <Label className="text-xs uppercase tracking-wider">Facultades adicionales</Label>
                  <p className="text-[10px] text-zinc-400 mt-1 mb-2">Permisos finos que se suman a los roles. Útil para dar acceso puntual sin cambiar el rol completo.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {ALL_CAPABILITIES.map((c) => {
                      const checked = (form.capabilities || []).includes(c.key);
                      return (
                        <label key={c.key} className="flex items-start gap-2 cursor-pointer p-2.5 border border-zinc-200 rounded hover:bg-zinc-50 has-[:checked]:border-[#002FA7]/40 has-[:checked]:bg-[#002FA7]/5" data-testid={`user-cap-${c.key}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCap(c.key)}
                            className="w-4 h-4 mt-0.5 accent-[#002FA7] shrink-0"
                          />
                          <div className="min-w-0">
                            <div className="text-xs font-semibold leading-tight">{c.label}</div>
                            <div className="text-[10px] text-zinc-500 leading-snug mt-0.5">{c.description}</div>
                          </div>
                        </label>
                      );
                    })}
                    {ALL_CAPABILITIES.length === 0 && <p className="text-[11px] text-zinc-400 italic col-span-full">Sin facultades disponibles todavía.</p>}
                  </div>
                </div>
              </div>

              {/* Comisionista */}
              <div className="border border-zinc-200 rounded-sm p-4 bg-zinc-50/50 mt-2">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="user-is-commissioner-label">
                  <input
                    type="checkbox"
                    checked={!!form.is_commissioner}
                    onChange={(e) => setForm((f) => ({ ...f, is_commissioner: e.target.checked }))}
                    className="w-4 h-4 mt-0.5 accent-[#E91E63]"
                    data-testid="user-is-commissioner"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zinc-800">Es comisionista</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      Se le paga un porcentaje de las ventas que genera. Al activarlo se crea automáticamente un
                      <b> gasto recurrente semanal (cada sábado)</b> en la categoría {"\u201C"}Comisiones{"\u201D"}, con el monto sugerido
                      calculado dinámicamente desde las ventas cobradas de <b>domingo a sábado</b>. Solo confirmas el pago.
                    </div>
                  </div>
                </label>
                {form.is_commissioner && (
                  <div className="mt-3 flex items-center gap-3 pl-7">
                    <Label className="text-xs uppercase tracking-wider text-zinc-600 whitespace-nowrap">% Comisión</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.commission_pct}
                      onChange={(e) => setForm((f) => ({ ...f, commission_pct: e.target.value }))}
                      className="w-24 h-8"
                      data-testid="user-commission-pct"
                    />
                    <span className="text-xs text-zinc-500">% sobre ventas cobradas (default 10%)</span>
                  </div>
                )}
              </div>

              {!editingUserId && (
                <p className="text-xs text-zinc-500 bg-zinc-50 p-3 border border-zinc-200 rounded-sm">
                  Se generará una contraseña temporal. Cópiala y compártela con el usuario — solo se mostrará una vez. El usuario deberá cambiarla en su primer inicio de sesión.
                </p>
              )}
            </div>
          </form>
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 shrink-0 bg-white">
            <Button type="button" variant="outline" onClick={()=>{ setShowForm(false); setEditingUserId(null); setForm(EMPTY_FORM); }}>Cancelar</Button>
            <Button type="button" disabled={saving} onClick={create} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="user-create-submit">
              {saving ? "Guardando…" : editingUserId ? "Guardar cambios" : "Crear usuario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempInfo} onOpenChange={(o)=>{ if (!o) setTempInfo(null); }}>
        <DialogContent data-testid="temp-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Contraseña temporal generada</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-600">Compártela con el usuario. Al iniciar sesión se le pedirá cambiarla.</p>
            <div className="p-4 bg-zinc-900 text-white rounded-sm space-y-2">
              <div className="text-xs text-zinc-400 font-mono-data">EMAIL</div>
              <div className="font-mono-data text-sm">{tempInfo?.email}</div>
              <div className="text-xs text-zinc-400 font-mono-data mt-3">CONTRASEÑA</div>
              <div className="flex items-center gap-3">
                <code className="font-mono-data text-xl font-bold tracking-wider text-[#FFC000]">{tempInfo?.password}</code>
                <Button size="sm" variant="outline" className="bg-transparent border-zinc-600 text-white hover:bg-zinc-800" onClick={()=>copy(tempInfo.password)}>
                  <Copy size={14} className="mr-1" /> Copiar
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={()=>setTempInfo(null)} className="bg-[#002FA7] hover:bg-[#002277] text-white">Listo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showChangePwd} onOpenChange={(o)=>{ if (!o) { setShowChangePwd(false); setPwdForm({ current:"", next:"", confirm:"" }); } }}>
        <DialogContent data-testid="change-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Cambiar mi contraseña</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitChangePwd} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Contraseña actual</Label>
              <PasswordInput value={pwdForm.current} onChange={(e)=>setPwdForm({...pwdForm, current: e.target.value})} required autoComplete="current-password" data-testid="pwd-current" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nueva contraseña</Label>
              <PasswordInput value={pwdForm.next} onChange={(e)=>setPwdForm({...pwdForm, next: e.target.value})} required autoComplete="new-password" minLength={10} data-testid="pwd-new" className="mt-1" />
              <div className="text-[11px] text-zinc-500 mt-1">{PASSWORD_HINT}</div>
              <p className="text-[10px] text-zinc-400 mt-1">Mínimo 6 caracteres</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Confirmar nueva contraseña</Label>
              <PasswordInput value={pwdForm.confirm} onChange={(e)=>setPwdForm({...pwdForm, confirm: e.target.value})} required autoComplete="new-password" data-testid="pwd-confirm" className="mt-1" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>{ setShowChangePwd(false); setPwdForm({ current:"", next:"", confirm:"" }); }}>Cancelar</Button>
              <Button type="submit" disabled={pwdSaving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="pwd-submit">
                {pwdSaving ? "Guardando…" : "Cambiar contraseña"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará la cuenta de <b>{toDelete?.name}</b> ({toDelete?.email}). Esta acción es irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#E0000F] hover:bg-[#b8000c] text-white">Sí, eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UserSessions({ userId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get("/users/sessions", { params: { user_id: userId, limit: 100 } })
      .then((r) => setSessions(r.data || []))
      .finally(() => setLoading(false));
  }, [userId]);
  return (
    <div className="mt-4 space-y-2" data-testid="user-sessions-list">
      {loading && <p className="text-xs text-zinc-500">Cargando…</p>}
      {!loading && sessions.length === 0 && (
        <p className="text-sm text-zinc-400 dotted-bg py-8 text-center">Sin sesiones registradas para este usuario</p>
      )}
      {sessions.map((s) => (
        <div key={s.id} className="border border-zinc-200 rounded p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-mono-data text-xs text-zinc-700">{fmtDate(s.created_at)}</span>
            <span className="text-[10px] text-zinc-500 font-mono-data">{s.ip || "—"}</span>
          </div>
          <div className="text-xs text-zinc-600 mt-1 inline-flex items-center gap-1">
            <DesktopTower size={12} className="text-zinc-400" /> {parseDevice(s.user_agent)}
          </div>
          {s.user_agent && (
            <p className="text-[10px] text-zinc-400 mt-0.5 truncate" title={s.user_agent}>{s.user_agent}</p>
          )}
        </div>
      ))}
    </div>
  );
}
